import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import clsx from 'clsx';
import Modal from 'components/shared/modals/Modal'
import Tabs from 'components/shared/Tabs';
import Input from 'components/shared/Input';
import { ReactComponent as IconSearch } from 'images/search.svg';
import useUserFollowersFollowingCount from 'sdk/hooks/follow/useUserFollowersFollowingCount';
import useUserFollowersList from 'sdk/hooks/follow/useUserFollowersList';
import useFollowingAssets from 'sdk/hooks/follow/useFollowingAssets';
import useUserFollowingList from 'sdk/hooks/follow/useUserFollowingList';
import useUserPrivacyActivities from 'sdk/hooks/user/useUserPrivacyActivities';
import NoPermission from 'components/shared/NoContent/NoPermission';
import NoContent from 'components/shared/NoContent/NoContent';
import useProfileById from 'sdk/hooks/user/useProfileById';
import useUser from 'sdk/hooks/user/useProfile';
import SignUp from 'modules/auth/components/SignUp';
import Follower from '../Follower/Follower';
import FollowingAsset from '../FollowingAsset/FollowingAsset';

import styles from './FollowersModal.module.scss';

type Props = {
  isOpen: boolean
  onClose: () => void
  userId: string
  profileId?: string
  isOwnProfile?: boolean
  view: View
  setView: Dispatch<SetStateAction<View>>
}

enum View { Followers, Following, Assets }

export function FollowersModal({
  isOpen,
  onClose,
  userId,
  profileId,
  isOwnProfile,
  view,
  setView,
}: Props) {
  const { data: settings } = useUserPrivacyActivities(userId)
  const { data: count, refetch: refetchCount } = useUserFollowersFollowingCount(userId)
  const [isSignUpVisible, setSignUpVisible] = useState(false)
  const [counts, setCounts] = useState<{
    followers: number | null;
    following: number | null;
    assets: number | null;
  }>()
  const [searchFollowers, setSearchFollowers] = useState('')
  const [searchFollowings, setSearchFollowings] = useState('')
  const [searchAssets, setSearchAssets] = useState('')

  const {
    data: followers, refetch:
    refetchFollowers,
  } = useUserFollowersList(userId, 100, searchFollowers)

  const {
    data: following,
    refetch: refetchFollowings,
  } = useUserFollowingList(userId, 100, searchFollowings)
  // const {
  //   data: followingAssets,
  //   refetch: refetchFollowingsAssets,
  // } = useFollowingAssets(userId, searchAssets)

  const { data: profile } = useProfileById(userId)
  let content: ReactNode = null;
  const { data: currentUser } = useUser()

  useEffect(() => {
    refetchFollowers()
  }, [isOpen, searchFollowers])

  useEffect(() => {
    refetchFollowings()
  }, [isOpen, searchFollowings])

  // useEffect(() => {
  //   refetchFollowingsAssets()
  // }, [isOpen, searchAssets])

  useEffect(() => {
    if (count) {
      setCounts(count)
    }
  }, [count])

  const getTitle = (subject: string) => {
    if (subject === 'followers') {
      return `${(counts?.followers || '0')} Followers`
    }
    if (subject === 'assets') {
      return `${(counts?.assets || '0')} Watching`
    }
    return `${(counts?.following || '0')} Following`
  }

  const items = [
    { title: getTitle('followers'), id: View.Followers, isActive: count?.followers !== 0 || currentUser?.id === userId },
    { title: getTitle('followings'), id: View.Following, isActive: count?.following !== 0 || currentUser?.id === userId },
    // { title: getTitle('assets'), id: View.Assets, isActive: followingAssets?.length !== 0 || currentUser?.id === userId },
  ]

  if (view === View.Followers) {
    if (settings?.following === false && !isOwnProfile) {
      content = (
        <NoPermission
          title="This user’s followers are private"
          message={(
            <>
              Users followers by
              {' '}
              <span style={{ color: '#fff' }}>
                @
                {profile?.username}
              </span>
              {' '}
              are currently hidden
            </>
      )}
        />
      )
    } else {
      content = counts?.followers ? (
        <div className={clsx(styles.followerList, 'scroll')}>
          {
          followers?.pages[0]?.map((follower, index) => (
            <Follower
              key={index as any}
              follower={follower}
              profileId={profileId}
              isMyProfile={isOwnProfile}
              setSignUpVisible={() => setSignUpVisible(true)}
              refetchFollowers={() => {
                refetchFollowers()
                refetchCount()
              }}
              setCounts={setCounts}
              mode="followers" /> // eslint-disable-line
          ))
        }
        </div>
      ) : (
        <NoContent title="No data" message="There are no followers" />
      )
    }
  } else if (view === View.Following) {
    if (settings?.following === false && !isOwnProfile) {
      content = (
        <NoPermission
          title="This user’s followings are private"
          message={(
            <>
              Users followings by
              {' '}
              <span style={{ color: '#fff' }}>
                @
                {profile?.username}
              </span>
              {' '}
              are currently hidden
            </>
)}
        />
      )
    } else {
      content = counts?.following ? (
        <div className={clsx(styles.followerList, 'scroll')}>
          {
            following?.pages[0]?.map((follower, index) => (
              <Follower
                key={index as any}
                follower={follower as any}
                profileId={profileId}
                setSignUpVisible={() => setSignUpVisible(true)}
                refetchFollowers={() => {
                  refetchFollowers()
                  refetchCount()
                }}
                isMyProfile={isOwnProfile}
                setCounts={setCounts}
                mode="following" /> // eslint-disable-line
            ))
          }
        </div>
      ) : (
        <NoContent title="No data" message="There are no followings" />
      )
    }
  } else if (settings?.assets === false && !isOwnProfile) {
    content = (
      <NoPermission
        title="This user’s followed assets are private"
        message={(
          <>
            Assets followed by
            {' '}
            <span style={{ color: '#fff' }}>
              @
              {profile?.username}
            </span>
            {' '}
            are currently hidden
          </>
)}
      />
    )
  } else {
    content = counts?.assets ? (
      <div className={clsx(styles.followerList, 'scroll')}>
        <table className={styles.assetTable}>
          <tbody>
            <tr className={styles.assetTr}>
              <th>Name</th>
              <th />
              {isOwnProfile && <th />}
              <th>Price</th>
              <th />
            </tr>
            {/* {
              followingAssets?.map((asset) => (
                <FollowingAsset
                  asset={asset}
                  setSignUpVisible={() => setSignUpVisible(true)}
                  isOwnProfile={isOwnProfile}
                  setCounts={setCounts} /> // eslint-disable-line

              ))
            } */}
          </tbody>
        </table>
      </div>
    ) : (
      <NoContent title="No data" message="There are no assets" />
    )
  }

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>, searchType: 'followers' | 'followings' | 'assets') => {
    if (searchType === 'followers') {
      setSearchFollowers((e.target as HTMLInputElement).value)
    }
    if (searchType === 'followings') {
      setSearchFollowings((e.target as HTMLInputElement).value)
    }
    if (searchType === 'assets') {
      setSearchAssets((e.target as HTMLInputElement).value)
    }
  }

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      onClick={(e: any) => e.stopPropagation()}
      className={styles.followModal}
    >
      <Tabs
        className={styles.tabs}
        items={items}
        onSelect={setView}
        value={view}
        inline
      />
      {
        view === 0 && (count?.followers as number) > 5 ? (
          <Input
            icon={<IconSearch />}
            className={styles.search}
            placeholder="Search"
            value={searchFollowers}
            onChange={(e) => handleSearchChange(e, 'followers')}
          />
        ) : null
      }
      {
        view === 1 && (count?.following as number) > 5 ? (
          <Input
            icon={<IconSearch />}
            className={styles.search}
            placeholder="Search"
            value={searchFollowings}
            onChange={(e) => handleSearchChange(e, 'followings')}
          />
        ) : null
      }
      {
        view === 2 && (count?.assets as number) > 5 ? (
          <Input
            icon={<IconSearch />}
            className={styles.search}
            placeholder="Search"
            value={searchAssets}
            onChange={(e) => handleSearchChange(e, 'assets')}
          />
        ) : null
      }

      {content}
      {isSignUpVisible ? (
        <SignUp
          onClose={() => {
            setSignUpVisible(false)
          }}
          goToSignIn={() => {
            setSignUpVisible(false)
          }}
        />
      ) : null}
    </Modal>
  )
}
